import { checkServerHealth } from '@wiztime/sync';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/Button';
import styles from './Sync.module.css';
import { isValidServerUrl, serverUrlProblem, ServerUrlProblem, warnsInsecure } from './urls';

export const HealthState = { Idle: 'idle', Checking: 'checking', Ok: 'ok', Failed: 'failed' } as const;
export type HealthState = (typeof HealthState)[keyof typeof HealthState];

interface ServerUrlFieldProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
}

/** The sync server address (default: this site) with a connection check. */
export function ServerUrlField({ value, onChange }: ServerUrlFieldProps) {
  const { t } = useTranslation();
  const [health, setHealth] = useState<HealthState>(() =>
    isValidServerUrl(value) ? HealthState.Checking : HealthState.Idle,
  );
  // Only the latest check may set the result: a slow answer for an old address must not overwrite a newer one.
  const latest = useRef(0);
  const problem = serverUrlProblem(value);
  const valid = problem === null;
  const insecure = warnsInsecure(value);
  const [initialUrl] = useState(value);

  const check = (address: string): void => {
    const id = ++latest.current;
    if (!isValidServerUrl(address)) {
      setHealth(HealthState.Idle);
      return;
    }
    setHealth(HealthState.Checking);
    void checkServerHealth(address).then((ok) => {
      if (id === latest.current) setHealth(ok ? HealthState.Ok : HealthState.Failed);
    });
  };

  // Check the address the form opens with; later checks follow edits (blur) and the button.
  useEffect(() => {
    const id = ++latest.current;
    void checkServerHealth(initialUrl).then((ok) => {
      if (id === latest.current) setHealth(ok ? HealthState.Ok : HealthState.Failed);
    });
  }, [initialUrl]);

  return (
    <div className={styles.serverField}>
      <label htmlFor="sync-server-url">{t('sync.server.label')}</label>
      <div className={styles.serverRow}>
        <input
          id="sync-server-url"
          type="url"
          value={value}
          aria-invalid={!valid}
          autoComplete="off"
          spellCheck={false}
          data-testid="sync-server-url"
          onChange={(event) => {
            onChange(event.target.value);
            setHealth(HealthState.Idle);
          }}
          onBlur={() => check(value)}
        />
        <Button
          disabled={!valid || health === HealthState.Checking}
          onClick={() => check(value)}
          data-testid="sync-check"
        >
          {t('sync.server.check')}
        </Button>
      </div>
      <p className={styles.healthLine} aria-live="polite" data-testid="sync-health" data-health={health}>
        {!valid ? (
          <span className={styles.bad}>
            {problem === ServerUrlProblem.BlockedInsecure
              ? t('sync.server.insecureBlocked')
              : t('sync.server.invalid')}
          </span>
        ) : health === HealthState.Checking ? (
          <span className={styles.muted}>{t('sync.server.checking')}</span>
        ) : health === HealthState.Ok ? (
          <span className={styles.good}>{t('sync.server.ok')}</span>
        ) : health === HealthState.Failed ? (
          <span className={styles.bad}>{t('sync.server.failed')}</span>
        ) : (
          <span className={styles.muted}>{t('sync.server.hint')}</span>
        )}
      </p>
      {insecure ? (
        <p className={styles.warning} role="note" data-testid="sync-insecure">
          {t('sync.server.insecure')}
        </p>
      ) : null}
    </div>
  );
}
